export type SystemAdminDrugRow = {
    id: number;
    name: string;
    animal_group: string;
    administration_method: string;
    created_at: string;
    /** Null for drugs loaded by script, which carry no creating user. */
    created_by_name: string | null;
};

/** Every field of one drug; reference columns arrive as translated text, optional columns as null. */
export type SystemAdminDrugDetail = SystemAdminDrugRow & {
    instructions: string | null;
    storage_info: string | null;
    diagnosis: string | null;
    dosage_unit: string | null;
    dosage: number | null;
    repetitions: number | null;
    repetition_spacing: number | null;
    retention_time: number | null;
};

export type SystemAdminDrugPage = {
    page_number: number;
    page_size: number;
    next_page: number | null;
    total_pages: number | null;
    total_rows: number | null;
    data: SystemAdminDrugRow[];
};

export type SystemAdminDrugListParams = {
    search: string;
    sortBy: 'Name' | 'CreatedAt';
    descending: boolean;
    animalGroupIds: string[];
    administrationMethodIds: string[];
    pageNumber: number;
    pageSize: number;
};

/** Selectable reference rows; ids are numeric strings, as every Kvp list on the API. */
export type SystemAdminDrugLookups = {
    animal_groups: { id: string; value: string }[];
    administration_methods: { id: string; value: string }[];
    dosage_units: { id: string; value: string }[];
};

/** Mirrors the `Health.Drug` columns the form fills; name, animal group and administration method are NOT NULL in the database. */
export type SystemAdminDrugForm = {
    name: string;
    animal_group_id: string | null;
    administration_method_id: string | null;
    instructions: string | null;
    storage_info: string | null;
    diagnosis: string | null;
    dosage: number | string | null;
    dosage_unit_id: string | null;
    repetitions: number | string | null;
    repetition_spacing: number | string | null;
    retention_time: number | string | null;
};

export type SystemAdminDrugCreatePayload = {
    name: string;
    animal_group_id: number | null;
    administration_method_id: number | null;
    instructions: string | null;
    storage_info: string | null;
    diagnosis: string | null;
    dosage: number | null;
    dosage_unit_id: number | null;
    repetitions: number | null;
    repetition_spacing: number | null;
    retention_time: number | null;
};

function toNumberOrNull(value: number | string | null) {
    return value === null || value === '' ? null : Number(value);
}

function toTextOrNull(value: string | null) {
    return value === null || value.trim() === '' ? null : value;
}

/** Wire payload for creating a drug: trimmed name, blank optional fields as null, numeric fields as numbers. */
export function toSystemAdminDrugCreatePayload(form: SystemAdminDrugForm): SystemAdminDrugCreatePayload {
    return {
        name: form.name.trim(),
        animal_group_id: toNumberOrNull(form.animal_group_id),
        administration_method_id: toNumberOrNull(form.administration_method_id),
        instructions: toTextOrNull(form.instructions),
        storage_info: toTextOrNull(form.storage_info),
        diagnosis: toTextOrNull(form.diagnosis),
        dosage: toNumberOrNull(form.dosage),
        dosage_unit_id: toNumberOrNull(form.dosage_unit_id),
        repetitions: toNumberOrNull(form.repetitions),
        repetition_spacing: toNumberOrNull(form.repetition_spacing),
        retention_time: toNumberOrNull(form.retention_time),
    };
}
